/** Varias conversaciones abiertas a la vez, sin tapar el panel.  (spec 073) */

export interface HiloFlotante {
    accountId: number
    conversationId: number
    minimizado: boolean
    x: number
    y: number
    z: number
    /** Firma de los mensajes ya vistos. null hasta la primera lectura. */
    firmaVista: string | null
    parpadea: boolean
}

export type AccionHilo =
    | { tipo: 'abrir'; accountId: number; conversationId: number; x: number; y: number }
    | { tipo: 'cerrar'; accountId: number; conversationId: number }
    | { tipo: 'minimizar'; accountId: number; conversationId: number }
    | { tipo: 'restaurar'; accountId: number; conversationId: number }
    | { tipo: 'frente'; accountId: number; conversationId: number }
    | { tipo: 'mover'; accountId: number; conversationId: number; x: number; y: number }
    | { tipo: 'visto'; accountId: number; conversationId: number; firma: string }
    | { tipo: 'comparar'; accountId: number; conversationId: number; firma: string }

export function claveHilo(accountId: number, conversationId: number): string {
    return `${accountId}:${conversationId}`
}

export function firmaMensajes(
    mensajes: readonly { direction: string; at: string | null }[],
): string {
    const ultimo = mensajes[mensajes.length - 1]
    if (!ultimo) return '0'
    return `${mensajes.length}:${ultimo.direction}:${ultimo.at ?? ''}`
}

export function posicionInicial(indice: number, ancho: number, alto: number): { x: number; y: number } {
    const margen = 16
    const w = ancho < 768 ? ancho - margen * 2 : 440
    const h = ancho < 768 ? alto - 160 : 620
    const columna = indice % 3
    const fila = Math.floor(indice / 3) % 2
    return {
        x: Math.max(margen, ancho - w - margen - columna * (Math.min(w, 360) + 16)),
        y: Math.max(64, alto - h - margen - fila * 36),
    }
}

function mismo(h: HiloFlotante, accountId: number, conversationId: number): boolean {
    return h.accountId === accountId && h.conversationId === conversationId
}

function zMax(lista: readonly HiloFlotante[]): number {
    return lista.reduce((m, h) => Math.max(m, h.z), 40)
}

/** Misma lista si ese hilo no cambió: el refresco de mensajes no puede re-dispararse solo. */
function sustituir(
    lista: readonly HiloFlotante[],
    accountId: number,
    conversationId: number,
    fn: (h: HiloFlotante) => HiloFlotante,
): HiloFlotante[] {
    let cambio = false
    const next = lista.map((h) => {
        if (!mismo(h, accountId, conversationId)) return h
        const n = fn(h)
        if (n !== h) cambio = true
        return n
    })
    return cambio ? next : (lista as HiloFlotante[])
}

export function reducirHilos(lista: readonly HiloFlotante[], accion: AccionHilo): HiloFlotante[] {
    if (accion.tipo === 'abrir') {
        const ya = lista.find((h) => mismo(h, accion.accountId, accion.conversationId))
        const z = zMax(lista) + 1
        if (ya) {
            return sustituir(lista, accion.accountId, accion.conversationId, (h) =>
                h.minimizado === false && h.parpadea === false && h.z === z
                    ? h
                    : { ...h, minimizado: false, parpadea: false, z },
            )
        }
        return [
            ...lista,
            {
                accountId: accion.accountId,
                conversationId: accion.conversationId,
                minimizado: false,
                x: accion.x,
                y: accion.y,
                z,
                firmaVista: null,
                parpadea: false,
            },
        ]
    }
    if (accion.tipo === 'cerrar') {
        return lista.filter((h) => !mismo(h, accion.accountId, accion.conversationId))
    }
    if (accion.tipo === 'minimizar') {
        return sustituir(lista, accion.accountId, accion.conversationId, (h) =>
            h.minimizado ? h : { ...h, minimizado: true },
        )
    }
    if (accion.tipo === 'restaurar' || accion.tipo === 'frente') {
        const z = zMax(lista) + 1
        const abrir = accion.tipo === 'restaurar'
        return sustituir(lista, accion.accountId, accion.conversationId, (h) => {
            const minimizado = abrir ? false : h.minimizado
            if (h.minimizado === minimizado && h.parpadea === false && h.z === z) return h
            return { ...h, minimizado, parpadea: false, z }
        })
    }
    if (accion.tipo === 'mover') {
        return sustituir(lista, accion.accountId, accion.conversationId, (h) =>
            h.x === accion.x && h.y === accion.y ? h : { ...h, x: accion.x, y: accion.y },
        )
    }
    if (accion.tipo === 'visto') {
        return sustituir(lista, accion.accountId, accion.conversationId, (h) =>
            h.firmaVista === accion.firma && h.parpadea === false
                ? h
                : { ...h, firmaVista: accion.firma, parpadea: false },
        )
    }
    return sustituir(lista, accion.accountId, accion.conversationId, (h) => {
        if (!h.minimizado) return h
        if (h.firmaVista === null) return { ...h, firmaVista: accion.firma }
        if (h.firmaVista === accion.firma) return h
        if (h.parpadea) return h
        return { ...h, parpadea: true }
    })
}
